import { useEffect, useMemo, useRef, useState, type DragEvent, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { ApiClientError } from '../api/http'
import { Button } from '../components/ui/Button'
import { Card } from '../components/ui/Card'
import { Icon, I } from '../components/ui/icons'
import { Pill } from '../components/ui/Pill'
import { gridColumns, mobileButtonStyle, TEXT_WRAP_STYLE } from '../components/ui'
import { useIsMobileViewport } from '../hooks/useIsMobileViewport'
import { createProduct, deleteProductImageObject, getProduct, presignProductImage, updateProduct } from '../products/productsApi'
import type { Product, ProductImageInput, ProductPayload, ProductStatus } from '../products/types'

const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp']
const CLIENT_MAX_IMAGE_BYTES = 5 * 1024 * 1024
const MAX_PRODUCT_IMAGES = 10
const MAX_SKU_LENGTH = 128
const STATUS_OPTIONS: ProductStatus[] = ['ACTIVE', 'DRAFT', 'INACTIVE']

interface FormState {
  sku: string
  name: string
  description: string
  status: ProductStatus
  price: string
  cost: string
  salePrice: string
  weightGrams: string
  taxRate: string
}

interface FormImage {
  clientId: string
  key: string
  publicUrl: string | null
  altText: string
  sortOrder: number
  primary: boolean
  isNewUpload: boolean
}

function emptyState(): FormState {
  return {
    sku: '',
    name: '',
    description: '',
    status: 'DRAFT',
    price: '',
    cost: '',
    salePrice: '',
    weightGrams: '',
    taxRate: '',
  }
}

function decimalOrNull(value: string): number | null {
  if (!value.trim()) return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function decimalOrZero(value: string): number {
  return decimalOrNull(value) ?? 0
}

function apiErrorKey(error: unknown): string {
  if (error instanceof ApiClientError) return `pages.products.errors.codes.${error.code}`
  return 'pages.products.errors.codes.REQUEST_FAILED'
}

function fileValidationKey(file: File): string | null {
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) return 'PRODUCT_IMAGE_TYPE_UNSUPPORTED'
  if (file.size > CLIENT_MAX_IMAGE_BYTES) return 'PRODUCT_IMAGE_TOO_LARGE'
  return null
}

function reorderImages(images: FormImage[]): FormImage[] {
  const normalized = images.map((image, index) => ({ ...image, sortOrder: index }))
  if (normalized.length > 0 && !normalized.some(image => image.primary)) {
    normalized[0] = { ...normalized[0], primary: true }
  }
  return normalized
}

function payloadImages(images: FormImage[]): ProductImageInput[] {
  return reorderImages(images).map((image, index) => ({
    key: image.key,
    altText: image.altText.trim() || null,
    sortOrder: index,
    primary: image.primary,
  }))
}

function TextAreaField({ label, value, onChange, placeholder }: { label: string; value: string; onChange: (value: string) => void; placeholder?: string }) {
  return (
    <label style={{ display: 'block' }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg)', marginBottom: 6 }}>{label}</div>
      <textarea
        value={value}
        onChange={event => onChange(event.target.value)}
        placeholder={placeholder}
        rows={4}
        style={{
          width: '100%', padding: '11px 14px', fontSize: 14, fontFamily: 'inherit', color: 'var(--fg)',
          background: 'white', border: '1px solid var(--border-default)', borderRadius: 10, outline: 'none',
          resize: 'vertical', lineHeight: 1.5,
        }}
      />
    </label>
  )
}

function InputField({ label, value, onChange, type = 'text', required, suffix }: { label: string; value: string; onChange: (value: string) => void; type?: string; required?: boolean; suffix?: string }) {
  return (
    <label style={{ display: 'block' }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg)', marginBottom: 6 }}>{label}</div>
      <div style={{ position: 'relative' }}>
        <input
          value={value}
          onChange={event => onChange(event.target.value)}
          type={type}
          required={required}
          min={type === 'number' ? '0' : undefined}
          step={type === 'number' ? '0.01' : undefined}
          style={{
            width: '100%', paddingBlock: '11px', paddingInlineStart: 14, paddingInlineEnd: suffix ? 70 : 14,
            fontSize: 14, fontFamily: 'inherit', color: 'var(--fg)', background: 'white',
            border: '1px solid var(--border-default)', borderRadius: 10, outline: 'none',
          }}
        />
        {suffix && <span style={{ position: 'absolute', insetInlineEnd: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 12, color: 'var(--fg-subtle)' }}>{suffix}</span>}
      </div>
    </label>
  )
}

interface ProductFormPageProps {
  accessToken: string
  mode: 'create' | 'edit'
  productId?: number | null
  onCancel?: () => void
  onSaved?: (productId: number) => void
}

export function ProductFormPage({ accessToken, mode, productId, onCancel, onSaved }: ProductFormPageProps) {
  const { t } = useTranslation()
  const isMobile = useIsMobileViewport()
  const inputRef = useRef<HTMLInputElement | null>(null)
  const uploadedKeysRef = useRef<Set<string>>(new Set())
  const committedRef = useRef(false)
  const [form, setForm] = useState<FormState>(emptyState)
  const [images, setImages] = useState<FormImage[]>([])
  const [altSkus, setAltSkus] = useState<string[]>([])
  const [loading, setLoading] = useState(mode === 'edit')
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [errorKey, setErrorKey] = useState<string | null>(null)
  const [imageErrorKey, setImageErrorKey] = useState<string | null>(null)

  const titleKey = mode === 'create' ? 'pages.productForm.createTitle' : 'pages.productForm.editTitle'
  const submitKey = mode === 'create' ? 'pages.productForm.actions.create' : 'pages.productForm.actions.save'

  useEffect(() => {
    if (mode !== 'edit' || !productId) return
    let cancelled = false
    // Intentional reset on productId change so the form re-enters its loading
    // state when navigating between edit targets. The set-state-in-effect rule's
    // general guidance doesn't fit this canonical fetch-on-deps pattern.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoading(true)
    setErrorKey(null)
    getProduct(accessToken, productId)
      .then((product: Product) => {
        if (cancelled) return
        setForm({
          sku: product.sku,
          name: product.name,
          description: product.description ?? '',
          status: product.status,
          price: String(product.price ?? ''),
          cost: product.cost == null ? '' : String(product.cost),
          salePrice: product.salePrice == null ? '' : String(product.salePrice),
          weightGrams: product.weightGrams == null ? '' : String(product.weightGrams),
          taxRate: product.taxRate == null ? '' : String(product.taxRate),
        })
        setAltSkus(product.altSkus ?? [])
        setImages(reorderImages(product.images.map((image, index) => ({
          clientId: `${image.id ?? image.key}-${index}`,
          key: image.key,
          publicUrl: image.publicUrl,
          altText: image.altText ?? '',
          sortOrder: image.sortOrder,
          primary: image.primary,
          isNewUpload: false,
        }))))
      })
      .catch(caught => {
        if (!cancelled) setErrorKey(apiErrorKey(caught))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [accessToken, mode, productId])

  const newUploadKeys = useMemo(() => images.filter(image => image.isNewUpload).map(image => image.key), [images])

  useEffect(() => {
    uploadedKeysRef.current = new Set(newUploadKeys)
  }, [newUploadKeys])

  useEffect(() => {
    return () => {
      if (committedRef.current) return
      const keys = Array.from(uploadedKeysRef.current)
      keys.forEach(key => {
        void deleteProductImageObject(accessToken, key).catch(() => undefined)
      })
      uploadedKeysRef.current.clear()
    }
  }, [accessToken])

  function updateField(key: keyof FormState, value: string) {
    setForm(current => ({ ...current, [key]: value }))
  }

  async function cleanupNewUploads(keys = Array.from(uploadedKeysRef.current)) {
    uploadedKeysRef.current.clear()
    await Promise.all(keys.map(key => deleteProductImageObject(accessToken, key).catch(() => undefined)))
  }

  async function handleCancel() {
    await cleanupNewUploads()
    onCancel?.()
  }

  async function handleFiles(fileList: FileList | File[]) {
    const files = Array.from(fileList)
    if (files.length === 0) return
    setImageErrorKey(null)

    let projectedImageCount = images.length

    for (const file of files) {
      const validationCode = fileValidationKey(file)
      if (validationCode) {
        setImageErrorKey(`pages.productForm.images.errors.${validationCode}`)
        continue
      }
      if (projectedImageCount >= MAX_PRODUCT_IMAGES) {
        setImageErrorKey('pages.products.errors.codes.PRODUCT_IMAGE_LIMIT_EXCEEDED')
        continue
      }

      setUploading(true)
      try {
        const presign = await presignProductImage(accessToken, {
          fileName: file.name,
          contentType: file.type,
          byteSize: file.size,
        })
        const uploadResponse = await fetch(presign.uploadUrl, {
          method: 'PUT',
          headers: presign.requiredHeaders,
          body: file,
        })
        if (!uploadResponse.ok) {
          throw new Error('UPLOAD_FAILED')
        }
        projectedImageCount += 1
        uploadedKeysRef.current.add(presign.key)
        setImages(current => reorderImages([
          ...current,
          {
            clientId: `${Date.now()}-${file.name}`,
            key: presign.key,
            publicUrl: presign.publicUrl,
            altText: '',
            sortOrder: current.length,
            primary: current.length === 0,
            isNewUpload: true,
          },
        ]))
      } catch (caught) {
        setImageErrorKey(caught instanceof ApiClientError ? `pages.products.errors.codes.${caught.code}` : 'pages.productForm.images.errors.uploadFailed')
      } finally {
        setUploading(false)
      }
    }
  }

  function handleDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault()
    void handleFiles(event.dataTransfer.files)
  }

  function setPrimary(clientId: string) {
    setImages(current => current.map(image => ({ ...image, primary: image.clientId === clientId })))
  }

  function moveImage(clientId: string, direction: -1 | 1) {
    setImages(current => {
      const index = current.findIndex(image => image.clientId === clientId)
      const target = index + direction
      if (index < 0 || target < 0 || target >= current.length) return current
      const next = [...current]
      const [image] = next.splice(index, 1)
      next.splice(target, 0, image)
      return reorderImages(next)
    })
  }

  function validateAltSkus(): boolean {
    const seen = new Set<string>()
    for (const sku of altSkus) {
      const normalized = sku.trim()
      const lower = normalized.toLowerCase()
      if (!normalized || normalized.length > MAX_SKU_LENGTH || seen.has(lower) || lower === form.sku.trim().toLowerCase()) {
        setErrorKey('pages.productForm.validation.altSkuUnique')
        return false
      }
      seen.add(lower)
    }
    return true
  }

  function removeImage(clientId: string) {
    const image = images.find(item => item.clientId === clientId)
    if (image?.isNewUpload) {
      void deleteProductImageObject(accessToken, image.key).catch(() => undefined)
    }
    setImages(current => reorderImages(current.filter(item => item.clientId !== clientId)))
  }

  function buildPayload(): ProductPayload {
    return {
      sku: form.sku.trim(),
      name: form.name.trim(),
      description: form.description.trim() || null,
      status: form.status,
      price: decimalOrZero(form.price),
      cost: decimalOrNull(form.cost),
      salePrice: decimalOrNull(form.salePrice),
      weightGrams: decimalOrNull(form.weightGrams),
      taxRate: decimalOrNull(form.taxRate),
      images: payloadImages(images),
      altSkus: altSkus.map(sku => sku.trim()),
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!form.sku.trim() || !form.name.trim()) {
      setErrorKey('pages.productForm.validation.required')
      return
    }
    if (!validateAltSkus()) return

    setSaving(true)
    setErrorKey(null)
    try {
      const saved = mode === 'edit' && productId
        ? await updateProduct(accessToken, productId, buildPayload())
        : await createProduct(accessToken, buildPayload())
      committedRef.current = true
      uploadedKeysRef.current.clear()
      onSaved?.(saved.id)
    } catch (caught) {
      setErrorKey(apiErrorKey(caught))
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <Card padding={22}><div style={{ color: 'var(--fg-muted)', fontSize: 13, fontWeight: 700 }}>{t('pages.productForm.loading')}</div></Card>
  }

  return (
    <form onSubmit={handleSubmit}>
      <div style={{ display: isMobile ? 'grid' : 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 18 }}>
        <div style={TEXT_WRAP_STYLE}>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: 'var(--brand-900)' }}>{t(titleKey)}</h2>
          <p style={{ margin: '6px 0 0', color: 'var(--fg-muted)', fontSize: 13 }}>{t('pages.productForm.subtitle')}</p>
        </div>
        <div style={{ display: isMobile ? 'grid' : 'flex', gap: 8 }}>
          <Button kind="secondary" size="sm" onClick={handleCancel} disabled={saving} style={mobileButtonStyle(isMobile)}>{t('pages.productForm.actions.cancel')}</Button>
          <Button kind="primary" size="sm" type="submit" disabled={saving || uploading} style={mobileButtonStyle(isMobile)}>{saving ? t('pages.productForm.actions.saving') : t(submitKey)}</Button>
        </div>
      </div>

      {errorKey && (
        <Card padding={16} style={{ marginBottom: 16, borderColor: 'var(--danger-100)' }}>
          <div style={{ color: 'var(--danger)', fontWeight: 700, fontSize: 13 }}>{String(t(errorKey, { defaultValue: String(t('pages.products.errors.codes.REQUEST_FAILED')) }))}</div>
        </Card>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: gridColumns(isMobile, 'minmax(0, 1.2fr) minmax(0, 0.8fr)'), gap: 18, alignItems: 'start' }}>
        <Card padding={22}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--fg-subtle)', marginBottom: 16 }}>{t('pages.productForm.sections.master')}</div>
          <div style={{ display: 'grid', gridTemplateColumns: gridColumns(isMobile, 'repeat(2, minmax(0, 1fr))'), gap: 14 }}>
            <InputField label={t('pages.productForm.fields.sku')} value={form.sku} onChange={value => updateField('sku', value)} required />
            <InputField label={t('pages.productForm.fields.name')} value={form.name} onChange={value => updateField('name', value)} required />
            <label style={{ display: 'block' }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg)', marginBottom: 6 }}>{t('pages.productForm.fields.status')}</div>
              <select
                value={form.status}
                onChange={event => updateField('status', event.target.value as ProductStatus)}
                style={{ width: '100%', padding: '11px 14px', fontSize: 14, fontFamily: 'inherit', color: 'var(--fg)', background: 'white', border: '1px solid var(--border-default)', borderRadius: 10, outline: 'none' }}
              >
                {STATUS_OPTIONS.map(status => <option key={status} value={status}>{t(`pages.products.status.${status}`)}</option>)}
              </select>
            </label>
            <InputField label={t('pages.productForm.fields.price')} value={form.price} onChange={value => updateField('price', value)} type="number" required suffix={t('pages.productForm.units.sar')} />
            <InputField label={t('pages.productForm.fields.cost')} value={form.cost} onChange={value => updateField('cost', value)} type="number" suffix={t('pages.productForm.units.sar')} />
            <InputField label={t('pages.productForm.fields.salePrice')} value={form.salePrice} onChange={value => updateField('salePrice', value)} type="number" suffix={t('pages.productForm.units.sar')} />
            <InputField label={t('pages.productForm.fields.weightGrams')} value={form.weightGrams} onChange={value => updateField('weightGrams', value)} type="number" suffix={t('pages.productForm.units.grams')} />
            <InputField label={t('pages.productForm.fields.taxRate')} value={form.taxRate} onChange={value => updateField('taxRate', value)} type="number" suffix={t('pages.productForm.units.percent')} />
          </div>
          <div style={{ marginTop: 18, paddingTop: 16, borderTop: '1px solid var(--border-subtle)' }}>
            <div style={{ display: isMobile ? 'grid' : 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'center', gap: 12, marginBottom: 10 }}>
              <div style={TEXT_WRAP_STYLE}>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--fg)' }}>{t('pages.productForm.altSkus.title')}</div>
                <div style={{ fontSize: 12, color: 'var(--fg-subtle)', marginTop: 2 }}>{t('pages.productForm.altSkus.placeholder')}</div>
              </div>
              <Button kind="secondary" size="xs" icon={I.plus} onClick={() => setAltSkus(current => [...current, ''])} style={mobileButtonStyle(isMobile)}>{t('pages.productForm.altSkus.add')}</Button>
            </div>
            {altSkus.length > 0 && (
              <div style={{ display: 'grid', gap: 8 }}>
                {altSkus.map((sku, index) => (
                  <div key={index} style={{ display: 'grid', gridTemplateColumns: isMobile ? 'minmax(0, 1fr)' : 'minmax(0, 1fr) auto', gap: 8 }}>
                    <input
                      aria-label={`${t('pages.productForm.altSkus.title')} ${index + 1}`}
                      value={sku}
                      onChange={event => setAltSkus(current => current.map((item, itemIndex) => itemIndex === index ? event.target.value : item))}
                      placeholder={t('pages.productForm.altSkus.placeholder')}
                      maxLength={MAX_SKU_LENGTH}
                      style={{ width: '100%', paddingBlock: '9px', paddingInline: 12, fontSize: 13, fontFamily: 'var(--font-mono)', color: 'var(--fg)', background: 'white', border: '1px solid var(--border-default)', borderRadius: 10, outline: 'none' }}
                    />
                    <Button kind="ghost" size="xs" icon={I.x} onClick={() => setAltSkus(current => current.filter((_, itemIndex) => itemIndex !== index))} style={mobileButtonStyle(isMobile)}>{t('pages.productForm.altSkus.remove')}</Button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div style={{ marginTop: 14 }}>
            <TextAreaField label={t('pages.productForm.fields.description')} value={form.description} onChange={value => updateField('description', value)} placeholder={t('pages.productForm.fields.descriptionPlaceholder')} />
          </div>
        </Card>

        <Card padding={22}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--fg-subtle)' }}>{t('pages.productForm.sections.images')}</div>
            {uploading && <Pill tone="info" size="sm">{t('pages.productForm.images.uploading')}</Pill>}
          </div>

          <div
            onDrop={handleDrop}
            onDragOver={event => event.preventDefault()}
            style={{ border: '1px dashed var(--border-default)', borderRadius: 14, padding: 18, textAlign: 'center', background: 'var(--ink-50)', marginBottom: 14 }}
          >
            <Icon size={24} color="var(--brand-600)">{I.download}</Icon>
            <div style={{ marginTop: 8, color: 'var(--brand-900)', fontWeight: 800, fontSize: 14 }}>{t('pages.productForm.images.dropTitle')}</div>
            <div style={{ marginTop: 4, color: 'var(--fg-muted)', fontSize: 12.5 }}>{t('pages.productForm.images.helper')}</div>
            <input
              ref={inputRef}
              aria-label={t('pages.productForm.images.fileInput')}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              onChange={event => {
                if (event.target.files) void handleFiles(event.target.files)
                event.currentTarget.value = ''
              }}
              style={{ display: 'none' }}
            />
            <Button kind="secondary" size="sm" onClick={() => inputRef.current?.click()} disabled={uploading} style={{ marginTop: 12, ...(mobileButtonStyle(isMobile) ?? {}) }}>{t('pages.productForm.images.chooseFiles')}</Button>
          </div>

          {imageErrorKey && <div style={{ color: 'var(--danger)', fontSize: 12.5, fontWeight: 700, marginBottom: 12 }}>{String(t(imageErrorKey, { defaultValue: String(t('pages.productForm.images.errors.uploadFailed')) }))}</div>}

          {images.length === 0 ? (
            <div style={{ color: 'var(--fg-subtle)', fontSize: 13, textAlign: 'center', padding: 18 }}>{t('pages.productForm.images.empty')}</div>
          ) : (
            <div style={{ display: 'grid', gap: 10 }}>
              {images.map((image, index) => (
                <div key={image.clientId} style={{ display: 'grid', gridTemplateColumns: isMobile ? 'minmax(0, 1fr)' : '58px minmax(0, 1fr)', gap: 10, padding: 10, border: '1px solid var(--border-subtle)', borderRadius: 12, background: 'white' }}>
                  {image.publicUrl
                    ? <img src={image.publicUrl} alt={image.altText || form.name || form.sku} style={{ width: 58, height: 58, borderRadius: 10, objectFit: 'cover' }} />
                    : <div style={{ width: 58, height: 58, borderRadius: 10, background: 'var(--ink-100)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon size={20}>{I.package}</Icon></div>}
                  <div style={TEXT_WRAP_STYLE}>
                    <input
                      value={image.altText}
                      onChange={event => setImages(current => current.map(item => item.clientId === image.clientId ? { ...item, altText: event.target.value } : item))}
                      placeholder={t('pages.productForm.images.altText')}
                      style={{ width: '100%', padding: '7px 9px', fontSize: 12.5, fontFamily: 'inherit', border: '1px solid var(--border-subtle)', borderRadius: 8, outline: 'none' }}
                    />
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                      <Button kind={image.primary ? 'primary' : 'secondary'} size="xs" onClick={() => setPrimary(image.clientId)} style={mobileButtonStyle(isMobile)}>{image.primary ? t('pages.productForm.images.primary') : t('pages.productForm.images.setPrimary')}</Button>
                      <Button kind="secondary" size="xs" disabled={index === 0} onClick={() => moveImage(image.clientId, -1)} style={mobileButtonStyle(isMobile)}>{t('pages.productForm.images.moveUp')}</Button>
                      <Button kind="secondary" size="xs" disabled={index === images.length - 1} onClick={() => moveImage(image.clientId, 1)} style={mobileButtonStyle(isMobile)}>{t('pages.productForm.images.moveDown')}</Button>
                      <Button kind="danger" size="xs" onClick={() => removeImage(image.clientId)} style={mobileButtonStyle(isMobile)}>{t('pages.productForm.images.remove')}</Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </form>
  )
}
